import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { HomePage } from "./pages/HomePage";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { OverviewPage } from "./pages/dashboard/OverviewPage";
import { PlaceholderPage } from "./pages/dashboard/PlaceholderPage";
import { ModelsListPage } from "./pages/dashboard/ModelsListPage";
import { ModelNewPage } from "./pages/dashboard/ModelNewPage";
import { ModelEditorPage } from "./pages/dashboard/ModelEditorPage";
import { ProjectsListPage } from "./pages/dashboard/ProjectsListPage";
import { ProjectDetailPage } from "./pages/dashboard/ProjectDetailPage";
import { AdvancesListPage } from "./pages/dashboard/AdvancesListPage";
import { AdvanceDetailPage } from "./pages/dashboard/AdvanceDetailPage";
import { PaymentsListPage } from "./pages/dashboard/PaymentsListPage";
import { InvoiceDetailPage } from "./pages/dashboard/InvoiceDetailPage";
import { DocumentsListPage } from "./pages/dashboard/DocumentsListPage";
import { MessagesListPage } from "./pages/dashboard/MessagesListPage";
import { MessagesChatPage } from "./pages/dashboard/MessagesChatPage";
import { TokenWalletPage } from "./pages/dashboard/TokenWalletPage";
import { ChangesListPage } from "./pages/dashboard/ChangesListPage";
import { ChangeDetailPage } from "./pages/dashboard/ChangeDetailPage";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<OverviewPage />} />
        <Route path="projects" element={<ProjectsListPage />} />
        <Route path="projects/:id" element={<ProjectDetailPage />} />
        <Route path="advances" element={<AdvancesListPage />} />
        <Route path="advances/:id" element={<AdvanceDetailPage />} />
        <Route path="payments" element={<PaymentsListPage />} />
        <Route path="payments/:id" element={<InvoiceDetailPage />} />
        <Route path="documents" element={<DocumentsListPage />} />
        <Route path="messages" element={<MessagesListPage />} />
        <Route path="messages/:projectId" element={<MessagesChatPage />} />
        <Route path="tokens" element={<TokenWalletPage />} />
        <Route path="changes" element={<ChangesListPage />} />
        <Route path="changes/:id" element={<ChangeDetailPage />} />
        <Route path="settings" element={<PlaceholderPage title="Configuración" icon="⚙️" />} />
        <Route path="models" element={<ModelsListPage />} />
        <Route path="models/new" element={<ModelNewPage />} />
        <Route path="models/:id" element={<ModelEditorPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}